import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import { apiErrorText } from '../../api/apiError';
import DialogActionsBar from '../../components/shared/DialogActionsBar';
import {
  useResolveHeldDietOrder,
  type DietOrderActionResult,
  type HeldDietOrder,
} from '../../api/dietOrderLockApi';
import RadioGroupField from '../../components/form/RadioGroupField';
import './HeldEhrChangesDialog.css';

interface Props {
  open: boolean;
  onClose: () => void;
  facilityId: number;
  patientId: number;
  held: HeldDietOrder[];
  /** The resident's current diet order, as shown in the grid. */
  currentDietOrder: string;
  ehrTitle: string;
  residentLabel: string;
  /** Diet_Fluctuations_Level >= 2; the API refuses the save otherwise. */
  canEdit: boolean;
  /** After a save, so the page can run AIM when the current diet changed. */
  onResolved: (result: DietOrderActionResult, useNew: boolean) => void;
}

function fmtReceived(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Review of held EHR diet orders: orders the EHR sent while the
 * resident's diet order was locked (tblDiet_Flucs_Lock, Completed = 0).
 * WinForms reviews them one at a time from the Meal Pattern "pending
 * diet" cards (frmDietech_Prd_DietOrderLock_Process): use the new diet
 * order and remove the lock, or keep the current one. Either way the
 * held order is marked reviewed and the choice is logged server-side.
 */
export default function HeldEhrChangesDialog({
  open,
  onClose,
  facilityId,
  patientId,
  held,
  currentDietOrder,
  ehrTitle,
  residentLabel,
  canEdit,
  onResolved,
}: Props) {
  const resolve = useResolveHeldDietOrder(facilityId, patientId);
  const [selectedKey, setSelectedKey] = useState<number | null>(null);
  const [choice, setChoice] = useState<'new' | 'keep' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One held order: pick it for the user. Re-seed when the list changes
  // (after a save the reviewed order drops out).
  useEffect(() => {
    if (!open) return;
    if (selectedKey != null && held.some((h) => h.pKey_Diet_Flucs_Lock === selectedKey)) return;
    setSelectedKey(held.length === 1 ? held[0].pKey_Diet_Flucs_Lock : null);
    setChoice(null);
  }, [open, held, selectedKey]);

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const selected = held.find((h) => h.pKey_Diet_Flucs_Lock === selectedKey) ?? null;
  const canSave = canEdit && selected != null && choice != null && !resolve.isPending;

  const handleSave = () => {
    if (!selected || choice == null) return;
    setError(null);
    const useNew = choice === 'new';
    resolve
      .mutateAsync({ pKey_Diet_Flucs_Lock: selected.pKey_Diet_Flucs_Lock, useNew })
      .then((res) => {
        setSelectedKey(null);
        setChoice(null);
        onResolved(res, useNew);
        // Anything still held stays on screen for the next decision.
        if (held.length <= 1) onClose();
      })
      .catch((err) => setError(apiErrorText(err, 'The held diet order could not be saved.')));
  };

  const resident = residentLabel.toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={() => !resolve.isPending && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="held-ehr-title"
    >
      <DialogTitle id="held-ehr-title" sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <span style={{ flex: 1 }}>Held {ehrTitle} diet changes</span>
        <Tooltip title="Close">
          <IconButton size="small" aria-label="Close" onClick={onClose} disabled={resolve.isPending}>
            <i className="bi bi-x-lg" style={{ fontSize: 16 }} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 0 }}>
          This {resident}&apos;s diet order is locked, so diet changes from {ehrTitle} were held
          here for review instead of replacing it.
        </p>
        <p>
          <strong>Current diet order:</strong> {currentDietOrder || '(none)'}
        </p>

        {held.length === 0 ? (
          <p>There are no held {ehrTitle} diet changes.</p>
        ) : (
          <fieldset className="held-ehr-fieldset">
            <legend>Held diet order{held.length === 1 ? '' : 's'} from {ehrTitle}</legend>
            <RadioGroupField<number>
              direction="column"
              ariaLabel={`Held diet order${held.length === 1 ? '' : 's'} from ${ehrTitle}`}
              name="held-ehr-order"
              optionClassName="held-ehr-option"
              value={selectedKey}
              onValueChanged={setSelectedKey}
              options={held.map((h) => ({
                value: h.pKey_Diet_Flucs_Lock,
                label: (
                  <span>
                    <span className="held-ehr-diet">{h.newDietOrderText || '(none)'}</span>
                    <span className="held-ehr-received">Received {fmtReceived(h.date_Downloaded)}</span>
                  </span>
                ),
              }))}
            />
          </fieldset>
        )}

        {held.length > 0 && (
          <fieldset className="held-ehr-fieldset">
            <legend>What should happen to the selected order?</legend>
            <RadioGroupField<'new' | 'keep'>
              direction="column"
              ariaLabel="What should happen to the selected order?"
              name="held-ehr-choice"
              optionClassName="held-ehr-option"
              value={choice}
              onValueChanged={setChoice}
              options={[
                { value: 'new', label: 'Use the new diet order (removes the lock)' },
                { value: 'keep', label: 'Keep the current diet order' },
              ]}
            />
          </fieldset>
        )}

        {!canEdit && (
          <Alert severity="info" sx={{ mt: 1 }}>
            You can view held diet changes, but your role can&apos;t change diet orders.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} role="alert">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActionsBar
        onCancel={onClose}
        onSave={handleSave}
        saveDisabled={!canSave}
        saving={resolve.isPending}
      />
    </Dialog>
  );
}
